import { useCallback } from 'react'

import {
  type CarrierRating,
  type CoiSpeed,
  type InsuranceType,
  type QuoteCardData,
  QuoteCardStage,
} from '@/components/quotes/quoteCardTypes'
import { type InsuranceCarrier, useInsuranceCarriers } from '@/models/insuranceCarrier'

// "Nirvana Insurance" and "Cover Whale" are the records "Nirvana" and "CoverWhale".
const normalize = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .replace(/insurance$/, '')

function findCarrier(carriers: InsuranceCarrier[], quote: QuoteCardData): InsuranceCarrier | undefined {
  const target = normalize(quote.carrierName)
  const program = quote.carrierProgram
  // A program names one paper of several, e.g. "Palomar Excess and Surplus (PESIC)".
  const byProgram =
    program &&
    carriers.find(
      (carrier) => normalize(carrier.name).startsWith(target) && carrier.name.includes(`(${program})`)
    )
  return (
    byProgram ||
    carriers.find((carrier) => normalize(carrier.name) === target) ||
    // A merged record keeps the old name as an alias, e.g. "Nirvana" on "Nirvana (Admitted)".
    carriers.find((carrier) => carrier.aliases.some((alias) => normalize(alias.name) === target))
  )
}

function hasClaims(carrier: InsuranceCarrier): boolean {
  return [
    carrier.claimsWaysToFile,
    carrier.claimsSupportAvailability,
    carrier.claimsPayoutSpeed,
    carrier.claimsWaysToGetPaid,
  ].some(Boolean)
}

/** Replaces a fixture's carrier details with its record's, blanks included, as the serializer does. */
function withCarrier(quote: QuoteCardData, carrier: InsuranceCarrier): QuoteCardData {
  const base = {
    ...quote,
    carrierRating: (carrier.rating || undefined) as CarrierRating | undefined,
    coiSpeed: (carrier.coiSpeed || undefined) as CoiSpeed | undefined,
    insuranceType: (carrier.admittedStatus || null) as InsuranceType | null,
    carrierPublicNote: carrier.publicNote || undefined,
  }
  if (base.stage !== QuoteCardStage.PRICED) return base
  return {
    ...base,
    // Plans come with the offer, not the record, and replace the record's text as the mapper does.
    financing: base.financing?.plans?.length
      ? base.financing
      : carrier.financingOptions
        ? { isStandard: true, description: carrier.financingOptions }
        : undefined,
    claimsCapabilities: hasClaims(carrier)
      ? {
          hasMultipleWaysToFile: carrier.claimsHasMultipleWaysToFile,
          waysToFile: carrier.claimsWaysToFile,
          has247Support: carrier.claimsHas247Support,
          supportAvailability: carrier.claimsSupportAvailability,
          hasPayoutInformation: carrier.claimsHasPayoutInformation,
          payoutSpeed: carrier.claimsPayoutSpeed,
          waysToGetPaid: carrier.claimsWaysToGetPaid,
        }
      : undefined,
  }
}

/** Fills fixtures' Carrier details from the carrier records the API serves; unmatched or offline keeps the fixture. */
export function useLiveCarrierDetails() {
  const { carriers } = useInsuranceCarriers()
  return useCallback(
    (quote: QuoteCardData): QuoteCardData => {
      const carrier = findCarrier(carriers, quote)
      return carrier ? withCarrier(quote, carrier) : quote
    },
    [carriers]
  )
}
